/**
 * Which mails have already been turned into an enquiry — answered on the mail screen.
 *
 * ---- Derived, never a stored flag ----
 *
 * `create_enquiry` records the `Communication` it was created from, and `get_enquiries` returns
 * that `mail_id` on every list row (85 of 99 carry one; the other 14 were keyed in by hand). The
 * answer already exists in the data and only needed reading back.
 *
 * A "processed" flag on the Communication would be a second source of truth for the same fact, and
 * would go stale the first time someone deleted an enquiry. This cannot: if the enquiry is gone,
 * the mail is no longer processed, immediately and without a migration.
 *
 * ---- One mail, many enquiries ----
 *
 * Measured 2026-09-03: 13 mails carry MORE than one enquiry, and one carries 24. Some of that is
 * intended — the multi-sheet split deliberately makes one enquiry per sheet — and some of it is
 * somebody pressing Create again because nothing on screen told them it had already been done,
 * which is the reason this module exists.
 *
 * So nothing here ever collapses to a single id. A badge reading "ENQ-2026-00100" on a mail that
 * has twenty-four of them would be the most confident kind of wrong.
 */

import type { EnquirySummary } from './types'

/** `Communication id → every enquiry created from it`, in the order the caller supplied. */
export function mailEnquiryIndex(enquiries: readonly EnquirySummary[]): Map<string, EnquirySummary[]> {
  const index = new Map<string, EnquirySummary[]>()

  for (const enquiry of enquiries) {
    const mailId = enquiry.mail_id ?? ''
    // A hand-keyed enquiry has no mail behind it. Left out rather than collected under an empty
    // key, which would otherwise mark some unrelated mail as processed.
    if (mailId === '') continue
    const existing = index.get(mailId)
    if (existing) existing.push(enquiry)
    else index.set(mailId, [enquiry])
  }

  return index
}

/**
 * The one line a mail row shows: the enquiry number, or how many there are and the newest.
 *
 * Twenty-four ids will not fit in a list row and would bury the subject if they did, so several
 * are counted and only the most recent named — the reader pane lists them in full.
 *
 * "Newest" is computed here rather than assumed from the caller's ordering: these ids are
 * sequential, so a plain string comparison is correct, and depending on the caller to have sorted
 * them is how the wrong number ends up on screen.
 */
/** The newest enquiry made from a mail — the one the row's badge opens (2026-09-18). */
export function latestProcessed(enquiries: readonly EnquirySummary[] | undefined): EnquirySummary | null {
  if (!enquiries || enquiries.length === 0) return null
  return enquiries.reduce((latest, e) => (e.id > latest.id ? e : latest))
}

export function describeProcessed(enquiries: readonly EnquirySummary[] | undefined): string | null {
  if (!enquiries || enquiries.length === 0) return null

  const newest = enquiries.reduce((latest, e) => (e.id > latest.id ? e : latest))
  if (enquiries.length === 1) return newest.id
  return `${enquiries.length} enquiries · latest ${newest.id}`
}
